import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { HbrSelect, HbrOption, HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { statusInfoSquare } from '@harbor/elements/icons';
import StbButton from '../security-toolbox-ui/StbButton';

interface ListWithMultiSelectItem {
  id: string | number;
  name: string;
  [key: string]: any;
}

interface ListWithMultiSelectProps {
  title: string;
  children: (item: ListWithMultiSelectItem, index: number) => ReactNode;
  selectedItems: ListWithMultiSelectItem[];
  optionItems: ListWithMultiSelectItem[];
  typeLabel: string;
  loading: boolean;
  onClick: (selectedItems: ListWithMultiSelectItem[]) => Promise<void> | void;
}

const ListWithMultiSelect = ({
                               title,
                               children,
                               selectedItems,
                               optionItems,
                               typeLabel,
                               loading,
                               onClick
                             }: ListWithMultiSelectProps) => {
  const [showField, setShowField] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const options = useMemo(() => {
    if (!optionItems) return [];
    return optionItems.map((c) => ({
      value: c.id.toString(),
      label: c.name,
    }));
  }, [optionItems]);

  const initialized = useMemo(() => {
    return selectedItems !== undefined;
  }, [selectedItems]);

  useEffect(() => {
    if (selectedItems) {
      setSelectedOptions(selectedItems.map((item) => item.id?.toString()));
    }
  }, [selectedItems]);

  const handleCancelClick = () => {
    setShowField(false);
    setSelectedOptions(selectedItems?.map((i) => i.id?.toString()) || []);
  };

  const handleSaveClick = async () => {
    await onClick(optionItems.filter((i) => selectedOptions.includes(i.id?.toString())));
    setShowField(false);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h3>{title}</h3>
      </div>

      {!initialized && (
        <HbrSpinner size="small" />
      )}

      {initialized && !loading && (!selectedItems || selectedItems?.length === 0) ? (
        <div className="bg-blue-100 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
          <HbrIcon asset={statusInfoSquare} sentiment="info" />
          No {typeLabel} found
        </div>
      ) : (
        <ul className="pl-5 mb-2">
          {selectedItems?.map((item, index) => children(item, index))}
        </ul>
      )}

      {showField ? (
        <div className="flex flex-col items-stretch gap-3 mb-4 pt-2 w-full min-w-0">
          <div className="w-full min-w-0">
            <HbrSelect
              placeholder={typeLabel}
              label={`Update ${typeLabel}`}
              variant="multiple"
              clearable
              value={selectedOptions}
              hoist
              sync="none"
              className="w-full min-w-0"
              onHbr-change={(event: any) => {
                const value = event.target?.value;
                setSelectedOptions(Array.isArray(value) ? value.map(String) : []);
              }}
            >
              {options.map((item) => (
                <HbrOption
                  key={item.value}
                  value={item.value}
                  display={item.label}
                >
                  {item.label}
                </HbrOption>
              ))}
              <span slot="counter">
                {selectedOptions.length}
              </span>
              <span slot="counter-label">
                {`Item${selectedOptions.length > 1 ? 's' : ''} selected`}
              </span>
            </HbrSelect>
          </div>

          <div className="flex flex-wrap gap-2">
            <StbButton
              role="save"
              size="small"
              onClick={handleSaveClick}
              disabled={loading}
            >
              {loading ? 'Updating...' : `Update ${typeLabel}`}
            </StbButton>
            <StbButton
              role="cancel"
              size="small"
              onClick={handleCancelClick}
              disabled={loading}
            >
              Close
            </StbButton>
          </div>
        </div>
      ) : (
        <StbButton
          role="update"
          onClick={() => {
            setShowField(true);
          }}
          size="small"
          className="mb-8 flex-start pt-2"
        >
          Edit {typeLabel}
        </StbButton>
      )}
    </>
  );
};

export default ListWithMultiSelect;